import { Show, useSelect } from "@refinedev/antd";
import { useShow, useCustomMutation, useCreate, useUpdate, useGetIdentity } from "@refinedev/core";
import { Card, Descriptions, Button, Empty, App, Modal, Form, Input, Row, Col, Select, Space, Statistic, Tag, Table, Tooltip, Typography } from "antd";
import {
  PlusOutlined,
  MailOutlined,
  ArrowLeftOutlined,
  SyncOutlined,
  HomeOutlined,
  AreaChartOutlined,
  TagsOutlined,
  UserOutlined,
  GlobalOutlined,
  IdcardOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router";
import { usePageTitle } from "../../hooks";
import dayjs from "dayjs";
import { FarmTable } from "./components/FarmTable";
import { ProducerMapCard } from "./components/ProducerMapCard";
import { PHONE_PREFIXES } from "./components/ContactFormList";
import { CertificationSyncModal } from "./components/CertificationSyncModal";
import { ContactChainsSection, type ProducerContactOption } from "./components/ContactChainsSection";
import { canWriteData, type UserInfo } from "../../types/auth";

interface ReferencePlot {
  id: number;
  surface: number;
  estimated_planting_date: string | null;
  geom: any;
}

interface Farm {
  id: number;
  name: string;
  province_name: string;
  town_name: string;
  surface_tango: number;
  audited_surface?: number;
  total_surface?: number;
  // [DEPRECATED] Pendiente de retirada en PR7 — se mantiene para el
  // FarmTable hasta que lo migremos a `varieties`.
  variety?: number;
  variety_display: string;
  // PR3 — Desglose por variedad agregado de las Plot activas de la
  // finca. Cada elemento es `{id, code, name, surface}`. Si una Plot
  // no tiene variedad asignada, aparece como entrada `id=null` /
  // `code='__none__'` y se pinta con un chip de aviso.
  varieties?: Array<{
    id: number | null;
    code: string;
    name: string;
    surface: number;
  }>;
  production_type: string;
  country?: number;
  province?: number;
  town?: number;
  reference_plots: ReferencePlot[];
}

interface Contact {
  id: string;
  contact: {
    id: string;
    name: string;
    surname: string;
    email: string;
    main_phone: string;
    secondary_phone: string | null;
    identification_number: string | null;
  };
  position: { id: number; name: string } | null;
  is_favorite: boolean;
  is_main_contact: boolean;
}

interface AuditSummary {
  total: number;
  finalized: number;
  last: {
    id: number;
    reference: string | null;
    start_date: string | null;
    audit_type_name: string | null;
    season_year: number | null;
    stimated_production_kg: number;
    audited_kg: number;
  } | null;
}

interface ProducerDetail {
  id: number;
  name: string;
  cif: string;
  portfolio: number | null;
  portfolio_name: string;
  agent: number | null;
  agent_name: string;
  country: number | null;
  country_display: string;
  created: string;
  modified: string;
  contacts: Contact[];
  farms: Farm[];
  audit_summary?: AuditSummary;
}

/** Parse a stored phone like "+34 600000000" into { prefix, number }. */
const parsePhone = (raw: string | null | undefined): { prefix: string; number: string } => {
  if (!raw) return { prefix: "+34", number: "" };
  const sorted = PHONE_PREFIXES.map((p) => p.value).sort((a, b) => b.length - a.length);
  for (const pfx of sorted) {
    if (raw.startsWith(pfx)) {
      return { prefix: pfx, number: raw.slice(pfx.length).trim() };
    }
  }
  return { prefix: "+34", number: raw };
};

/**
 * ProducerShow Component
 * Displays complete producer information with contacts and farms sections.
 */
export const ProducerShow = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { message, modal } = App.useApp();
  const { mutate: deleteFarm } = useCustomMutation();
  const { data: identity } = useGetIdentity<UserInfo>();
  const canWrite = canWriteData(identity);

  // Contact modal state
  const [contactModalVisible, setContactModalVisible] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [certSyncOpen, setCertSyncOpen] = useState(false);
  const [contactForm] = Form.useForm();
  const [contactSubmitting, setContactSubmitting] = useState(false);
  // Bumped after creating/editing/removing a contact so the
  // <ContactChainsSection key={chainsReloadToken}> remounts and
  // re-fetches the chains from the server.
  const [chainsReloadToken, setChainsReloadToken] = useState(0);
  const { mutate: createContact } = useCreate();
  const { mutate: updateContactMutation } = useUpdate();

  const { selectProps: positionSelectProps } = useSelect({
    resource: "business-positions",
    optionLabel: "name",
    optionValue: "id",
    pagination: { pageSize: 100 },
  });

  const { query } = useShow<ProducerDetail>({
    resource: "producers",
  });

  const { data, isLoading } = query;
  const record = data?.data;

  usePageTitle(record?.name || t("producers.show.title", "Detalle del Productor"));

  // Surface totals
  const totalLicensedSurface = (record?.farms || []).reduce((sum, f) => sum + (f.surface_tango || 0), 0);
  const totalAuditedSurface = (record?.farms || []).reduce((sum, f) => sum + (f.audited_surface || 0), 0);

  // Agregaciones para la cabecera del productor: nº fincas, variedades
  // únicas y superficie licenciada por variedad (Σ Plot.surface por
  // código de variedad, agregadas desde el desglose de cada finca).
  const farmsAgg = useMemo(() => {
    const farms = record?.farms || [];
    const active = farms.filter((f) => (f as any).is_active !== false).length;
    const total = farms.length;

    // Map<code, { name, code, licensed_ha }> agregando entre fincas.
    const byVariety = new Map<
      string,
      { code: string; name: string; licensed: number }
    >();
    for (const f of farms) {
      for (const v of f.varieties || []) {
        const key = v.code || "__none__";
        const entry =
          byVariety.get(key) ??
          { code: key, name: v.name || "(sin variedad)", licensed: 0 };
        entry.licensed += Number(v.surface) || 0;
        byVariety.set(key, entry);
      }
    }
    const varieties = Array.from(byVariety.values()).sort((a, b) => {
      // TANGO primero, sin variedad al final.
      const ta = a.code.toUpperCase() === "TANGO";
      const tb = b.code.toUpperCase() === "TANGO";
      if (ta !== tb) return ta ? -1 : 1;
      if (a.code === "__none__") return 1;
      if (b.code === "__none__") return -1;
      return b.licensed - a.licensed;
    });
    return { active, total, varieties };
  }, [record?.farms]);

  const surfaceDiff = totalAuditedSurface - totalLicensedSurface;

  // ProducerContacts del productor en el formato que espera el
  // <ContactChainsSection> para los selectores "+ contacto".
  const availableContacts = useMemo<ProducerContactOption[]>(
    () =>
      (record?.contacts ?? []).map((pc) => ({
        id: pc.id,
        name:
          `${pc.contact?.name ?? ""} ${pc.contact?.surname ?? ""}`.trim() ||
          pc.contact?.email ||
          "—",
        position: pc.position?.name ?? null,
        email: pc.contact?.email ?? null,
      })),
    [record?.contacts],
  );

  const handleEditFarm = (farm: any) => {
    navigate(`/producers/${record?.id}/farms/edit/${farm.id}`);
  };

  const handleDeactivateFarm = (farmId: number) => {
    modal.confirm({
      title: t("producers.farms.actions.deactivate", "Desactivar Finca"),
      content: t("producers.farms.messages.deactivate_confirm", "¿Está seguro de que desea desactivar esta finca? No se eliminará, pero dejará de estar activa."),
      okText: t("common.confirm", "Confirmar"),
      cancelText: t("common.cancel", "Cancelar"),
      onOk: () => {
        deleteFarm(
          { url: `/v1/farms/${farmId}/`, method: "patch", values: { is_active: false } },
          {
            onSuccess: () => {
              message.success(t("producers.farms.messages.deactivated_success", "Finca desactivada correctamente"));
              query.refetch();
            },
          },
        );
      },
    });
  };

  const handleActivateFarm = (farmId: number) => {
    modal.confirm({
      title: t("producers.farms.actions.activate", "Activar Finca"),
      content: t("producers.farms.messages.activate_confirm", "¿Está seguro de que desea activar esta finca?"),
      okText: t("common.confirm", "Confirmar"),
      cancelText: t("common.cancel", "Cancelar"),
      onOk: () => {
        deleteFarm(
          { url: `/v1/farms/${farmId}/`, method: "patch", values: { is_active: true } },
          {
            onSuccess: () => {
              message.success(t("producers.farms.messages.activated_success", "Finca activada correctamente"));
              query.refetch();
            },
          },
        );
      },
    });
  };

  // Toggle the manual "Necesita reauditoría" flag. When the next
  // planning is created and the farm shows up in "Fincas disponibles",
  // the suggested audit type will default to Completa instead of
  // Aforo. PATCH the v1 endpoint so the writable serializer applies.
  const handleToggleReaudit = (farmId: number, nextValue: boolean) => {
    modal.confirm({
      title: nextValue
        ? t(
            "producers.farms.actions.mark_reaudit",
            "Marcar para auditoría completa",
          )
        : t(
            "producers.farms.actions.clear_reaudit",
            "Quitar marca de reauditoría",
          ),
      content: nextValue
        ? t(
            "producers.farms.messages.mark_reaudit_confirm",
            "La finca quedará marcada como pendiente de auditoría completa. En la próxima planificación aparecerá con tipo sugerido «Completa».",
          )
        : t(
            "producers.farms.messages.clear_reaudit_confirm",
            "¿Quitar la marca de reauditoría completa?",
          ),
      okText: t("common.confirm", "Confirmar"),
      cancelText: t("common.cancel", "Cancelar"),
      onOk: () => {
        deleteFarm(
          {
            url: `/v1/farms/${farmId}/`,
            method: "patch",
            values: { needs_full_reaudit: nextValue },
          },
          {
            onSuccess: () => {
              message.success(
                nextValue
                  ? t(
                      "producers.farms.messages.marked_reaudit_success",
                      "Finca marcada para reauditoría",
                    )
                  : t(
                      "producers.farms.messages.cleared_reaudit_success",
                      "Marca de reauditoría retirada",
                    ),
              );
              query.refetch();
            },
          },
        );
      },
    });
  };

  // Contact modal handlers
  const openContactModal = (contact?: Contact) => {
    if (contact) {
      setEditingContact(contact);
      const mainPhone = parsePhone(contact.contact.main_phone);
      const secPhone = parsePhone(contact.contact.secondary_phone);
      contactForm.setFieldsValue({
        name: contact.contact.name,
        surname: contact.contact.surname,
        email: contact.contact.email,
        main_phone: mainPhone.number,
        main_phone_prefix: mainPhone.prefix,
        secondary_phone: secPhone.number,
        secondary_phone_prefix: secPhone.prefix,
        identification_number: contact.contact.identification_number,
        position_id: contact.position?.id || null,
      });
    } else {
      setEditingContact(null);
      contactForm.resetFields();
    }
    setContactModalVisible(true);
  };

  const handleContactSubmit = async () => {
    try {
      const values = await contactForm.validateFields();
      setContactSubmitting(true);

      const payload = {
        name: values.name,
        surname: values.surname,
        email: values.email || null,
        main_phone: values.main_phone ? `${values.main_phone_prefix || "+34"} ${values.main_phone}` : null,
        secondary_phone: values.secondary_phone ? `${values.secondary_phone_prefix || "+34"} ${values.secondary_phone}` : null,
        identification_number: values.identification_number || null,
        position_id: values.position_id || null,
      };

      if (editingContact) {
        updateContactMutation(
          { resource: "producer-contacts", id: editingContact.id, values: payload },
          {
            onSuccess: () => {
              setContactSubmitting(false);
              message.success(t("contacts.messages.updated_success", "Contacto actualizado correctamente"));
              setContactModalVisible(false);
              setEditingContact(null);
              contactForm.resetFields();
              query.refetch();
              setChainsReloadToken((n) => n + 1);
            },
            onError: () => { setContactSubmitting(false); },
          },
        );
      } else {
        createContact(
          { resource: `producers/${record?.id}/contacts`, values: payload },
          {
            onSuccess: () => {
              setContactSubmitting(false);
              message.success(t("contacts.messages.created_success", "Contacto creado correctamente"));
              setContactModalVisible(false);
              contactForm.resetFields();
              query.refetch();
              setChainsReloadToken((n) => n + 1);
            },
            onError: () => { setContactSubmitting(false); },
          },
        );
      }
    } catch {
      // validation errors shown inline
    }
  };

  const selectFilter = (input: string, option: any) =>
    String(option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  const prefixFilter = (input: string, option: any) =>
    String(option?.label ?? "").toLowerCase().includes(input.toLowerCase()) ||
    String(option?.value ?? "").includes(input);

  return (
    <Show
      isLoading={isLoading}
      // El nombre del productor es la identidad principal de esta
      // pantalla: sin él el usuario solo ve KPIs y secciones sin saber
      // a quién pertenecen. Mantenemos el ID a la derecha para que el
      // título también ayude a confirmar la entidad.
      title={
        record?.name ? (
          <Space size={8} align="center">
            <UserOutlined />
            <span>{record.name}</span>
            {record?.id != null && (
              <Typography.Text type="secondary" style={{ fontWeight: 400 }}>
                #{record.id}
              </Typography.Text>
            )}
          </Space>
        ) : undefined
      }
      headerButtons={() => {
        const state = location.state as any;
        const fromFarmId = state?.from === "farm" ? state?.farmId : null;
        // Cuando venimos del listado, `fromList` guarda el `location.search`
        // original (filtros + página + sort) para poder restaurarlo al
        // volver. Fallback: `?portfolio=X` calculado desde el record.
        const fromListSearch: string | undefined = state?.fromList;
        const backUrl = fromFarmId
          ? `/farms/show/${fromFarmId}`
          : fromListSearch !== undefined
            ? `/producers${fromListSearch}`
            : record?.portfolio
              ? `/producers?portfolio=${record.portfolio}`
              : "/producers";
        return (
          <>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(backUrl)}>
              {t("common.back", "Volver")}
            </Button>
            <Button
              icon={<SyncOutlined />}
              onClick={() => setCertSyncOpen(true)}
              disabled={!record?.id}
            >
              {t("certification_sync.button", "Sincronización")}
            </Button>
            {canWrite && (
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() =>
                  navigate(`/producers/edit/${record?.id}`, {
                    state: { fromShow: true, fromList: fromListSearch },
                  })
                }
                disabled={!record?.id}
              >
                {t("common.edit", "Editar")}
              </Button>
            )}
          </>
        );
      }}
      contentProps={{ style: { backgroundColor: "transparent", boxShadow: "none" } }}
    >
      {/* Row superior: Información del productor (50%) + Mapa (50%).
          Las secciones de contactos y fincas siguen a ancho completo
          abajo. */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <UserOutlined />
                <span>{t("producers.show.producer_info", "Información del Productor")}</span>
              </Space>
            }
            bordered={false}
            bodyStyle={{ padding: 12 }}
          >
            {/* KPIs destacados — 2 filas de 3 para añadir Auditorías
                + producción de la última auditoría sin apretar. */}
            <Row gutter={8} style={{ marginBottom: 8 }}>
              <Col xs={12} sm={8}>
                <Card size="small" bodyStyle={{ padding: "10px 12px" }}>
                  <Statistic
                    title="Fincas"
                    value={farmsAgg.active}
                    suffix={
                      farmsAgg.total > farmsAgg.active
                        ? `/ ${farmsAgg.total}`
                        : undefined
                    }
                    prefix={<HomeOutlined style={{ fontSize: 14 }} />}
                    valueStyle={{ fontSize: 18 }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={8}>
                <Card size="small" bodyStyle={{ padding: "10px 12px" }}>
                  <Statistic
                    title="Variedades"
                    value={farmsAgg.varieties.filter(v => v.code !== "__none__").length}
                    prefix={<TagsOutlined style={{ fontSize: 14 }} />}
                    valueStyle={{ fontSize: 18 }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={8}>
                <Card size="small" bodyStyle={{ padding: "10px 12px" }}>
                  <Statistic
                    title="Auditorías"
                    value={record?.audit_summary?.total ?? 0}
                    suffix={
                      record?.audit_summary?.finalized != null &&
                      record.audit_summary.total > 0
                        ? `(${record.audit_summary.finalized} fin)`
                        : undefined
                    }
                    valueStyle={{ fontSize: 18 }}
                  />
                </Card>
              </Col>
            </Row>
            <Row gutter={8} style={{ marginBottom: 12 }}>
              <Col xs={12} sm={8}>
                <Card size="small" bodyStyle={{ padding: "10px 12px" }}>
                  <Statistic
                    title="Sup. licenciada"
                    value={totalLicensedSurface}
                    precision={2}
                    suffix="ha"
                    valueStyle={{ fontSize: 18 }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={8}>
                <Card size="small" bodyStyle={{ padding: "10px 12px" }}>
                  <Statistic
                    title="Sup. auditada"
                    value={totalAuditedSurface}
                    precision={2}
                    suffix="ha"
                    valueStyle={{
                      fontSize: 18,
                      color:
                        Math.abs(surfaceDiff) < 0.01
                          ? undefined
                          : surfaceDiff >= 0
                            ? "#52c41a"
                            : "#cf1322",
                    }}
                  />
                </Card>
              </Col>
              <Col xs={12} sm={8}>
                <Card size="small" bodyStyle={{ padding: "10px 12px" }}>
                  <Statistic
                    title="Última producción"
                    value={record?.audit_summary?.last?.audited_kg ?? 0}
                    precision={0}
                    suffix="kg"
                    groupSeparator="."
                    valueStyle={{ fontSize: 18 }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Superficie por variedad */}
            <Card
              size="small"
              title={
                <Space size={6}>
                  <AreaChartOutlined />
                  <strong>Superficie por variedad</strong>
                </Space>
              }
              style={{ marginBottom: 12 }}
              bodyStyle={{ padding: 0 }}
            >
              {farmsAgg.varieties.length > 0 ? (
                <Table
                  size="small"
                  pagination={false}
                  dataSource={farmsAgg.varieties}
                  rowKey="code"
                  showHeader={false}
                >
                  <Table.Column
                    dataIndex="name"
                    render={(name: string, row: any) => {
                      const isTango = row.code?.toUpperCase() === "TANGO";
                      const isNone = row.code === "__none__";
                      return (
                        <Tag
                          color={
                            isNone ? "default" : isTango ? "orange" : "blue"
                          }
                          style={{ margin: 0 }}
                        >
                          {name}
                        </Tag>
                      );
                    }}
                  />
                  <Table.Column
                    dataIndex="licensed"
                    align="right"
                    width={130}
                    render={(v: number) => {
                      const pct =
                        totalLicensedSurface > 0
                          ? (v / totalLicensedSurface) * 100
                          : 0;
                      return (
                        <Tooltip title={`${pct.toFixed(1)}% del total`}>
                          <span>
                            <strong>{v.toFixed(2)}</strong> ha
                            <Typography.Text type="secondary" style={{ marginLeft: 6, fontSize: 11 }}>
                              {pct.toFixed(0)}%
                            </Typography.Text>
                          </span>
                        </Tooltip>
                      );
                    }}
                  />
                </Table>
              ) : (
                <div style={{ padding: 16 }}>
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Sin variedades declaradas"
                  />
                </div>
              )}
            </Card>

            {/* Última auditoría */}
            {record?.audit_summary?.last && (
              <Card
                size="small"
                title={
                  <Space size={6}>
                    <span>📋</span>
                    <strong>Última auditoría</strong>
                  </Space>
                }
                style={{ marginBottom: 12 }}
                bodyStyle={{ padding: "10px 12px" }}
              >
                <Row gutter={8}>
                  <Col span={12}>
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      REFERENCIA
                    </Typography.Text>
                    <div>
                      <a
                        onClick={() =>
                          navigate(`/audits/${record.audit_summary!.last!.id}`, {
                            state: { from: "producer" },
                          })
                        }
                        style={{ fontWeight: 500 }}
                      >
                        {record.audit_summary.last.reference || `#${record.audit_summary.last.id}`}
                      </a>
                      {record.audit_summary.last.audit_type_name && (
                        <Tag color="cyan" style={{ marginLeft: 6 }}>
                          {record.audit_summary.last.audit_type_name}
                        </Tag>
                      )}
                    </div>
                  </Col>
                  <Col span={12}>
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      FECHA
                    </Typography.Text>
                    <div>
                      {record.audit_summary.last.start_date
                        ? dayjs(record.audit_summary.last.start_date).format("DD/MM/YYYY")
                        : "—"}
                      {record.audit_summary.last.season_year && (
                        <Tag color="gold" style={{ marginLeft: 6 }}>
                          Campaña {record.audit_summary.last.season_year}
                        </Tag>
                      )}
                    </div>
                  </Col>
                </Row>
                <Row gutter={8} style={{ marginTop: 10 }}>
                  <Col span={12}>
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      ESTIMACIÓN PRODUCTOR
                    </Typography.Text>
                    <div style={{ fontWeight: 500 }}>
                      {record.audit_summary.last.stimated_production_kg
                        ? `${record.audit_summary.last.stimated_production_kg.toLocaleString("es-ES", { maximumFractionDigits: 0 })} kg`
                        : "—"}
                    </div>
                  </Col>
                  <Col span={12}>
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      AFORO AUDITOR
                    </Typography.Text>
                    <div style={{ fontWeight: 500 }}>
                      {record.audit_summary.last.audited_kg
                        ? `${record.audit_summary.last.audited_kg.toLocaleString("es-ES", { maximumFractionDigits: 0 })} kg`
                        : "—"}
                    </div>
                  </Col>
                </Row>
              </Card>
            )}

            {/* Metadatos del productor */}
            <Card
              size="small"
              title={
                <Space size={6}>
                  <IdcardOutlined />
                  <strong>Datos del productor</strong>
                </Space>
              }
              bodyStyle={{ padding: "8px 12px" }}
            >
              <Descriptions column={1} size="small" colon={false} labelStyle={{ color: "#8c8c8c", fontSize: 12, width: 140 }}>
                <Descriptions.Item label={t("producers.fields.cif", "CIF / RUC")}>
                  {record?.cif || "—"}
                </Descriptions.Item>
                <Descriptions.Item label={t("producers.fields.portfolio", "Cartera")}>
                  {record?.portfolio_name || "—"}
                </Descriptions.Item>
                <Descriptions.Item label={t("producers.fields.agent", "Responsable")}>
                  {record?.agent_name || "—"}
                </Descriptions.Item>
                <Descriptions.Item label={t("producers.fields.country", "País")}>
                  <Space size={4}>
                    <GlobalOutlined />
                    {record?.country_display || "—"}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label={t("producers.fields.created", "Creada")}>
                  {record?.created ? dayjs(record.created).format("DD/MM/YYYY") : "—"}
                </Descriptions.Item>
                <Descriptions.Item label={t("producers.fields.modified", "Modificada")}>
                  {record?.modified ? dayjs(record.modified).format("DD/MM/YYYY HH:mm") : "—"}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          {record?.id && record.farms && record.farms.length > 0 ? (
            <ProducerMapCard
              producerId={record.id}
              producerName={record.name}
            />
          ) : null}
        </Col>
      </Row>

      {/* Contacts Section — usa la misma sección de cadenas que la
          antigua página "Gestionar contactos". Cada cadena (License +
          una por finca) se renderiza en una mini-card; el "Nuevo
          Contacto" abre el modal de creación y, tras guardar, bumpea
          `chainsReloadToken` para forzar recarga del componente. */}
      <Card
        title={t("producers.show.contacts_section", "Contactos")}
        bordered={false}
        style={{ marginBottom: 24 }}
        extra={
          canWrite && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openContactModal()}>
              {t("producers.actions.create_contact", "Nuevo Contacto")}
            </Button>
          )
        }
      >
        {record?.id ? (
          <ContactChainsSection
            key={chainsReloadToken}
            producerId={record.id}
            availableContacts={availableContacts}
            grid
            bare
            canWrite={canWrite}
            onEditProducerContact={
              canWrite
                ? (pcId) => {
                    const target = record.contacts?.find((c) => c.id === pcId);
                    if (target) openContactModal(target);
                  }
                : undefined
            }
            onProducerContactDeleted={() => {
              query.refetch();
              setChainsReloadToken((n) => n + 1);
            }}
          />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("producers.show.no_contacts", "No hay contactos asociados")} />
        )}
      </Card>

      {/* Farms Section */}
      <Card
        title={t("producers.show.farms_section", "Fincas")}
        bordered={false}
        extra={
          canWrite && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate(`/producers/${record?.id}/farms/create`)}
            >
              {t("producers.actions.create_farm", "Crear Finca")}
            </Button>
          )
        }
      >
        {record?.farms && record.farms.length > 0 ? (
          <FarmTable
            farms={record.farms}
            onEdit={canWrite ? handleEditFarm : undefined}
            onDeactivate={canWrite ? handleDeactivateFarm : undefined}
            onActivate={canWrite ? handleActivateFarm : undefined}
            onToggleReaudit={canWrite ? handleToggleReaudit : undefined}
          />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("producers.show.no_farms", "No hay fincas asociadas")} />
        )}
      </Card>


      {/* Contact Create/Edit Modal */}
      <Modal
        title={
          editingContact
            ? t("contacts.actions.edit", "Editar Contacto")
            : t("contacts.actions.create", "Nuevo Contacto")
        }
        open={contactModalVisible}
        onOk={handleContactSubmit}
        onCancel={() => {
          setContactModalVisible(false);
          setEditingContact(null);
          contactForm.resetFields();
        }}
        confirmLoading={contactSubmitting}
        okText={t("common.save", "Guardar")}
        cancelText={t("common.cancel", "Cancelar")}
        width={640}
        destroyOnClose
      >
        <Form form={contactForm} layout="vertical" initialValues={{ main_phone_prefix: "+34", secondary_phone_prefix: "+34" }}>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="name"
                label={t("contacts.field.name", "Nombre")}
                rules={[{ required: true, message: t("contacts.error.required", "Campo requerido") }]}
              >
                <Input placeholder={t("contacts.placeholder.name", "Ingrese el nombre")} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="surname"
                label={t("contacts.field.surname", "Apellidos")}
                rules={[{ required: true, message: t("contacts.error.required", "Campo requerido") }]}
              >
                <Input placeholder={t("contacts.placeholder.surname", "Ingrese los apellidos")} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="email"
                label={t("contacts.field.email", "Email")}
                rules={[{ type: "email", message: t("contacts.error.invalid_email", "Formato de email inválido") }]}
              >
                <Input
                  prefix={<MailOutlined style={{ color: "#bfbfbf" }} />}
                  placeholder={t("contacts.placeholder.email", "ejemplo@correo.com")}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="main_phone" label={t("contacts.field.main_phone", "Teléfono Principal")}>
                <Input
                  placeholder={t("contacts.placeholder.main_phone", "600 000 000")}
                  addonBefore={
                    <Form.Item name="main_phone_prefix" noStyle>
                      <Select style={{ width: 100 }} options={PHONE_PREFIXES} showSearch filterOption={prefixFilter} getPopupContainer={(trigger) => trigger.closest(".ant-modal-body") || document.body} />
                    </Form.Item>
                  }
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="secondary_phone" label={t("contacts.field.secondary_phone", "Teléfono Secundario")}>
                <Input
                  placeholder={t("contacts.placeholder.secondary_phone", "700 000 000")}
                  addonBefore={
                    <Form.Item name="secondary_phone_prefix" noStyle>
                      <Select style={{ width: 100 }} options={PHONE_PREFIXES} showSearch filterOption={prefixFilter} getPopupContainer={(trigger) => trigger.closest(".ant-modal-body") || document.body} />
                    </Form.Item>
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="identification_number" label={t("contacts.field.identification_number", "Número de Identificación")}>
                <Input placeholder={t("contacts.placeholder.identification_number", "12345678A")} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="position_id" label={t("contacts.field.position", "Cargo/Posición")}>
                <Select
                  {...positionSelectProps}
                  placeholder={t("contacts.placeholder.position", "Seleccione un cargo")}
                  allowClear
                  showSearch
                  filterOption={selectFilter}
                  getPopupContainer={(trigger) => trigger.closest(".ant-modal-body") || document.body}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <CertificationSyncModal
        open={certSyncOpen}
        producerId={record?.id ?? null}
        producerName={record?.name}
        onClose={() => setCertSyncOpen(false)}
      />
    </Show>
  );
};
